import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_BOOKING_FOR_RECEIPT } from '../../graphql/queries/financeQueries';
import { formatBerlinDate, formatBerlinDateTimeRange } from '../../util/bookingDateTime';
import classes from './Receipt.module.css';
import backArrow from '../../public/icons/ZurückIcon.svg';

const formatDate = (dateString) => {
  return formatBerlinDate(dateString, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatDateTime = (start, end) => {
  const dateTime = formatBerlinDateTimeRange(start, end, {
    dateOptions: {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    },
  });

  return dateTime ? `${dateTime.date}, ${dateTime.time}` : '';
};

const formatPrice = (price) => {
  return price.toFixed(2).replace('.', ',') + ' €';
};

const formatPriceClean = (price) => {
  return price.toFixed(2).replace('.', ',');
};

const calculateHours = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate.getTime() - startDate.getTime();
  return Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10;
};

const Receipt = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const type = location.state?.type || 'payment';

  const { data, loading, error } = useQuery(GET_BOOKING_FOR_RECEIPT, {
    variables: { id: bookingId },
    skip: !bookingId,
    fetchPolicy: 'cache-and-network',
  });

  const booking = data?.getBooking;

  const handleDownloadPDF = () => {
    // For web, we'll generate HTML and use browser print
    if (!booking) return;

    const receiptNumber = booking.id.slice(0, 8).toUpperCase();
    const studioName = booking.Studio?.StudioName || 'Studio';
    const hours = calculateHours(booking.startDateTime, booking.endDateTime);
    const isIncome = type === 'income';

    const customerName = booking.User
      ? `${booking.User.firstName || ''} ${booking.User.lastName || ''}`.trim() || 'Kunde'
      : 'Kunde';

    const ownerName = booking.Studio?.User
      ? `${booking.Studio.User.firstName || ''} ${booking.Studio.User.lastName || ''}`.trim() || studioName
      : studioName;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Quittung ${receiptNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            color: #222222;
            line-height: 1.5;
            padding: 40px;
            max-width: 600px;
            margin: 0 auto;
          }
          .header {
            background: #1a1a1a;
            color: white;
            padding: 32px;
            text-align: center;
            border-radius: 12px 12px 0 0;
          }
          .logo { font-size: 36px; font-weight: 700; letter-spacing: -0.5px; }
          .receipt-type {
            font-size: 11px;
            letter-spacing: 3px;
            color: rgba(255,255,255,0.7);
            margin-top: 8px;
            text-transform: uppercase;
          }
          .meta-section {
            background: #fafafa;
            padding: 20px 24px;
            border-left: 1px solid #e0e0e0;
            border-right: 1px solid #e0e0e0;
          }
          .meta-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .meta-label { color: #888; font-size: 13px; }
          .meta-value { font-weight: 600; font-size: 13px; }
          .content {
            padding: 24px;
            border: 1px solid #e0e0e0;
            border-top: none;
          }
          .section { margin-bottom: 24px; }
          .section-title {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #1a1a1a;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 2px solid #1a1a1a;
          }
          .detail-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }
          .detail-label { color: #666; font-size: 14px; }
          .detail-value {
            font-weight: 500;
            font-size: 14px;
            text-align: right;
            max-width: 60%;
          }
          .price-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            border-bottom: 1px solid #eee;
          }
          .price-label { font-size: 14px; }
          .price-subtext {
            font-size: 12px;
            color: #888;
            margin-top: 2px;
          }
          .price-amount { font-weight: 600; font-size: 14px; }
          .total-section {
            background: #1a1a1a;
            color: white;
            padding: 24px;
            margin: 0 -24px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .total-label { font-size: 16px; font-weight: 600; }
          .total-amount { font-size: 28px; font-weight: 700; }
          .tax-note {
            font-size: 11px;
            color: rgba(255,255,255,0.6);
            margin-top: 4px;
          }
          .status-section { margin-top: 24px; }
          .status-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
          }
          .status-badge {
            background: #e8f5e9;
            color: #2e7d32;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 13px;
            font-weight: 600;
          }
          .status-badge.pending {
            background: #f5f5f5;
            color: #666;
          }
          .transaction-id {
            font-family: 'Courier New', monospace;
            font-size: 11px;
          }
          .footer {
            text-align: center;
            padding: 24px;
            border: 1px solid #e0e0e0;
            border-top: none;
            background: #fafafa;
            border-radius: 0 0 12px 12px;
          }
          .footer-text {
            font-size: 14px;
            font-weight: 500;
            color: #1a1a1a;
          }
          .footer-note {
            font-size: 12px;
            color: #888;
            margin-top: 4px;
          }
          .divider {
            height: 1px;
            background: #e0e0e0;
            margin: 20px 0;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Stu</div>
          <div class="receipt-type">${isIncome ? 'EINKÜNFTEBELEG' : 'ZAHLUNGSBELEG'}</div>
        </div>
        
        <div class="meta-section">
          <div class="meta-row">
            <span class="meta-label">Belegnummer</span>
            <span class="meta-value">${receiptNumber}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">Ausstellungsdatum</span>
            <span class="meta-value">${formatDate(booking.createdAt)}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">${isIncome ? 'Kunde' : 'Anbieter'}</span>
            <span class="meta-value">${isIncome ? customerName : ownerName}</span>
          </div>
        </div>
        
        <div class="content">
          <div class="section">
            <div class="section-title">Buchungsdetails</div>
            <div class="detail-row">
              <span class="detail-label">Studio</span>
              <span class="detail-value">${studioName}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Projektname</span>
              <span class="detail-value">${booking.projectName || '-'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Datum & Zeit</span>
              <span class="detail-value">${formatDateTime(booking.startDateTime, booking.endDateTime)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Dauer</span>
              <span class="detail-value">${hours} Stunden</span>
            </div>
            ${booking.numberOfGuests ? `
            <div class="detail-row">
              <span class="detail-label">Anzahl Gäste</span>
              <span class="detail-value">${booking.numberOfGuests}</span>
            </div>
            ` : ''}
          </div>
          
          <div class="divider"></div>
          
          <div class="section">
            <div class="section-title">Kostenaufstellung</div>
            <div class="price-row">
              <div>
                <div class="price-label">Studiomiete</div>
                <div class="price-subtext">${hours} Stunden${booking.Studio?.pricePerHour ? ` × ${formatPrice(booking.Studio.pricePerHour)}/Std.` : ''}</div>
              </div>
              <span class="price-amount">${formatPriceClean(booking.totalStudioPrice || booking.totalPrice)} €</span>
            </div>
            ${booking.totalCreativePrice > 0 ? `
            <div class="price-row">
              <div>
                <div class="price-label">Creative Services</div>
              </div>
              <span class="price-amount">${formatPriceClean(booking.totalCreativePrice)} €</span>
            </div>
            ` : ''}
          </div>
          
          <div class="total-section">
            <div class="total-row">
              <span class="total-label">Gesamtbetrag</span>
              <span class="total-amount">${formatPriceClean(booking.totalPrice)} €</span>
            </div>
            <div class="tax-note">inkl. MwSt.</div>
          </div>
          
          <div class="status-section">
            <div class="status-row">
              <span class="detail-label">Zahlungsstatus</span>
              <span class="status-badge ${booking.paymentStatus === 'PAID' ? '' : 'pending'}">
                ${booking.paymentStatus === 'PAID' ? '✓ Bezahlt' : 'Autorisiert'}
              </span>
            </div>
            ${booking.paymentIntentID ? `
            <div class="detail-row">
              <span class="detail-label">Transaktions-ID</span>
              <span class="detail-value transaction-id">...${booking.paymentIntentID.slice(-12)}</span>
            </div>
            ` : ''}
          </div>
        </div>
        
        <div class="footer">
          <div class="footer-text">Vielen Dank für Ihre Buchung über Stu!</div>
          <div class="footer-note">Bei Fragen wenden Sie sich an support@stu-studios.de</div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <button
            type="button"
            className={classes.backButton}
            onClick={() => navigate(-1)}
          >
            <img src={backArrow} alt="Zurück" />
          </button>
          <h1 className={classes.headerTitle}>Quittung</h1>
          <div className={classes.headerSpacer} />
        </div>
        <div className={classes.loadingContainer}>
          <div className={classes.loadingText}>Quittung wird geladen...</div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <button
            type="button"
            className={classes.backButton}
            onClick={() => navigate(-1)}
          >
            <img src={backArrow} alt="Zurück" />
          </button>
          <h1 className={classes.headerTitle}>Quittung</h1>
          <div className={classes.headerSpacer} />
        </div>
        <div className={classes.errorContainer}>
          <div className={classes.errorTitle}>Quittung konnte nicht geladen werden</div>
          <div className={classes.errorMessage}>
            {error?.message || 'Buchung nicht gefunden'}
          </div>
          <button
            type="button"
            className={classes.retryButton}
            onClick={() => window.location.reload()}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  const studioName = booking.Studio?.StudioName || 'Studio';
  const hours = calculateHours(booking.startDateTime, booking.endDateTime);
  const receiptNumber = booking.id.slice(0, 8).toUpperCase();
  const isIncome = type === 'income';

  const customerName = booking.User
    ? `${booking.User.firstName || ''} ${booking.User.lastName || ''}`.trim() || 'Kunde'
    : 'Kunde';

  const ownerName = booking.Studio?.User
    ? `${booking.Studio.User.firstName || ''} ${booking.Studio.User.lastName || ''}`.trim() || studioName
    : studioName;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <button
          type="button"
          className={classes.backButton}
          onClick={() => navigate(-1)}
        >
          <img src={backArrow} alt="Zurück" />
        </button>
        <h1 className={classes.headerTitle}>Quittung</h1>
        <div className={classes.headerSpacer} />
      </div>

      <div className={classes.scrollView}>
        <div className={classes.receiptCard}>
          <div className={classes.receiptHeader}>
            <div className={classes.logoText}>Stu</div>
            <div className={classes.receiptLabel}>
              {isIncome ? 'EINKÜNFTEBELEG' : 'ZAHLUNGSBELEG'}
            </div>
          </div>

          <div className={classes.receiptMeta}>
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>Belegnummer</span>
              <span className={classes.metaValue}>{receiptNumber}</span>
            </div>
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>Ausstellungsdatum</span>
              <span className={classes.metaValue}>{formatDate(booking.createdAt)}</span>
            </div>
            {isIncome ? (
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>Kunde</span>
                <span className={classes.metaValue}>{customerName}</span>
              </div>
            ) : (
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>Anbieter</span>
                <span className={classes.metaValue}>{ownerName}</span>
              </div>
            )}
          </div>

          <div className={classes.divider} />

          <div className={classes.section}>
            <div className={classes.sectionTitle}>Buchungsdetails</div>

            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Studio</span>
              <span className={classes.detailValue}>{studioName}</span>
            </div>

            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Projektname</span>
              <span className={classes.detailValue}>{booking.projectName || '-'}</span>
            </div>

            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Datum & Zeit</span>
              <span className={classes.detailValue}>
                {formatDateTime(booking.startDateTime, booking.endDateTime)}
              </span>
            </div>

            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Dauer</span>
              <span className={classes.detailValue}>{hours} Stunden</span>
            </div>

            {booking.numberOfGuests && (
              <div className={classes.detailRow}>
                <span className={classes.detailLabel}>Anzahl Gäste</span>
                <span className={classes.detailValue}>{booking.numberOfGuests}</span>
              </div>
            )}
          </div>

          <div className={classes.divider} />

          <div className={classes.section}>
            <div className={classes.sectionTitle}>Kostenaufstellung</div>

            <div className={classes.priceRow}>
              <div>
                <div className={classes.priceLabel}>Studiomiete</div>
                <div className={classes.priceSubtext}>
                  {hours} Stunden{booking.Studio?.pricePerHour ? ` × ${formatPrice(booking.Studio.pricePerHour)}/Std.` : ''}
                </div>
              </div>
              <span className={classes.priceAmount}>
                {formatPriceClean(booking.totalStudioPrice || booking.totalPrice)} €
              </span>
            </div>

            {booking.totalCreativePrice > 0 && (
              <div className={classes.priceRow}>
                <div>
                  <div className={classes.priceLabel}>Creative Services</div>
                </div>
                <span className={classes.priceAmount}>
                  {formatPriceClean(booking.totalCreativePrice)} €
                </span>
              </div>
            )}
          </div>

          <div className={classes.totalSection}>
            <div className={classes.totalRow}>
              <span className={classes.totalLabel}>Gesamtbetrag</span>
              <span className={classes.totalAmount}>
                {formatPriceClean(booking.totalPrice)} €
              </span>
            </div>
            <div className={classes.taxNote}>inkl. MwSt.</div>
          </div>

          <div className={classes.divider} />

          <div className={classes.paymentStatusSection}>
            <div className={classes.statusRow}>
              <span className={classes.statusLabel}>Zahlungsstatus</span>
              <div
                className={`${classes.statusBadge} ${
                  booking.paymentStatus === 'PAID' ? classes.statusPaid : ''
                }`}
              >
                <span
                  className={`${classes.statusText} ${
                    booking.paymentStatus === 'PAID' ? classes.statusTextPaid : ''
                  }`}
                >
                  {booking.paymentStatus === 'PAID' ? '✓ Bezahlt' : 'Autorisiert'}
                </span>
              </div>
            </div>

            {booking.paymentIntentID && (
              <div className={classes.detailRow}>
                <span className={classes.detailLabel}>Transaktions-ID</span>
                <span className={`${classes.detailValue} ${classes.transactionId}`}>
                  ...{booking.paymentIntentID.slice(-12)}
                </span>
              </div>
            )}
          </div>

          <div className={classes.receiptFooter}>
            <div className={classes.footerText}>
              Vielen Dank für Ihre Buchung über Stu!
            </div>
            <div className={classes.footerNote}>
              Bei Fragen wenden Sie sich an support@stu.app
            </div>
          </div>
        </div>
      </div>

      <div className={classes.actionBar}>
        <button
          type="button"
          className={classes.downloadButton}
          onClick={handleDownloadPDF}
        >
          <span className={classes.downloadIcon}>📥</span>
          <span className={classes.downloadButtonText}>PDF herunterladen</span>
        </button>
      </div>
    </div>
  );
};

export default Receipt;
